import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { Pager, usePaged } from '../components/Pager';
import { useCrumb } from '../lib/crumb';
import { relTime } from '../lib/useFreshness';
import { useUrlNumber, useUrlState } from '../lib/useUrlState';
import {
  ATLAS_AGENT_STALE_MIN,
  ATLAS_CAP,
  ATLAS_ENTRIES,
  ATLAS_HEARTBEATS,
  ATLAS_PENDING,
  PROJECTS_OVERVIEW,
  REFRESH_LIVE_COUNT,
  STATE_LABEL,
  STATE_TITLE,
  capacity,
  heartbeatColor,
  levelColor,
  minutesSince,
  type AtlasEntryRow,
  type AtlasHeartbeat,
  type AtlasProjectOverview,
} from '../lib/atlas';

/**
 * `/atlas-whitelist/:projectKey` — one project, capacity first.
 *
 * The page it replaces had five equal-weight `<h3>` sections and two visually
 * identical IP tables — "live Atlas list" and "reported IPs" — separately filtered,
 * sorted and paged. The only question an operator actually arrives with is *what is
 * missing, what is extra, what is stale*, and answering it meant diffing two
 * paginated tables by eye. `Reconcile now` acted on exactly that diff and never
 * showed it.
 *
 * So: one capacity number at the top, then ONE table whose `State` column is the
 * diff, and the things that break quietly — the cap and agent heartbeat — given
 * their own space instead of being derivable.
 */

const RECONCILE = gql`
  mutation ReconcileAtlasWhitelist($projectKey: String) {
    reconcileAtlasWhitelist(projectKey: $projectKey) {
      status
      reason
      projects {
        projectKey
        status
        added
        removed
        skipped
        error
      }
    }
  }
`;

const TEST = gql`
  mutation TestAtlasConnection($projectKey: String!) {
    testAtlasConnection(projectKey: $projectKey) {
      success
      error
    }
  }
`;

const SYNC_RUNS = gql`
  query AtlasProjectSyncRuns($projectKey: String, $limit: Int) {
    atlasSyncRuns(projectKey: $projectKey, limit: $limit) {
      id
      status
      added
      removed
      skipped
      errorMessage
      triggeredBy
      startedAt
      listSizeAfter
    }
  }
`;

const MAPPINGS = gql`
  query AtlasProjectMappings {
    clusterAtlasMappings {
      clusterName
      atlasProjectKey
    }
  }
`;

interface SyncRun {
  id: string;
  status: string;
  added: number;
  removed: number;
  skipped: number;
  errorMessage?: string | null;
  triggeredBy?: string | null;
  startedAt: string;
  listSizeAfter?: number | null;
}

interface PendingIp {
  id: string;
  ipCidr: string;
  clusterName?: string | null;
  nodeName?: string | null;
  lastSeenAt: string;
}

type Tab = 'list' | 'pending' | 'history';

export function AtlasProjectPage() {
  const { projectKey = '' } = useParams();
  useCrumb(projectKey || null);

  const [tab, setTab] = useUrlState('tab');
  const activeTab: Tab = tab === 'pending' || tab === 'history' ? tab : 'list';
  // Owned here rather than inside the table, because the capacity callout's
  // "Show N reclaimable entries" has to be able to set it — the whole point of
  // that button is to jump from the summary into the rows behind it.
  const [stateFilter, setStateFilter] = useUrlState('state', '', 'livepage');

  const overview = useQuery<{ atlasProjectsOverview: AtlasProjectOverview[] }>(PROJECTS_OVERVIEW, {
    fetchPolicy: 'cache-and-network',
  });
  const project = overview.data?.atlasProjectsOverview.find((p) => p.projectKey === projectKey);

  const entries = useQuery<{ atlasEntries: AtlasEntryRow[] }>(ATLAS_ENTRIES, {
    variables: { projectKey },
    skip: !projectKey || project?.hasCredentials === false,
    fetchPolicy: 'cache-and-network',
  });
  const pending = useQuery<{ atlasPendingIps: PendingIp[] }>(ATLAS_PENDING, {
    variables: { projectKey },
    skip: !projectKey,
    fetchPolicy: 'cache-and-network',
  });
  const runs = useQuery<{ atlasSyncRuns: SyncRun[] }>(SYNC_RUNS, {
    variables: { projectKey, limit: 15 },
    skip: !projectKey,
    fetchPolicy: 'cache-and-network',
  });
  const beats = useQuery<{ atlasAgentHeartbeats: AtlasHeartbeat[] }>(ATLAS_HEARTBEATS, {
    variables: { projectKey },
    skip: !projectKey,
    fetchPolicy: 'cache-and-network',
  });
  const maps = useQuery<{ clusterAtlasMappings: { clusterName: string; atlasProjectKey: string }[] }>(
    MAPPINGS,
    { fetchPolicy: 'cache-and-network' },
  );

  const refetchAll = () => {
    void overview.refetch();
    void entries.refetch();
    void pending.refetch();
    void runs.refetch();
  };

  const [reconcile, reconcileState] = useMutation(RECONCILE, { onCompleted: refetchAll });
  const [testConn, testState] = useMutation<{
    testAtlasConnection: { success: boolean; error?: string | null };
  }>(TEST);
  const [refresh, refreshState] = useMutation(REFRESH_LIVE_COUNT, { onCompleted: refetchAll });

  const rows = entries.data?.atlasEntries ?? [];
  const pendingRows = pending.data?.atlasPendingIps ?? [];
  const runRows = runs.data?.atlasSyncRuns ?? [];
  const mapped = (maps.data?.clusterAtlasMappings ?? [])
    .filter((m) => m.atlasProjectKey === projectKey)
    .map((m) => m.clusterName);

  // Counts come from the ENTRIES we actually have, not the snapshot, so the tab
  // label and the table can never disagree about what is on screen.
  const stateCounts = useMemo(() => {
    const out = { in_sync: 0, no_agent: 0, stale: 0, manual: 0, open_to_all: 0 };
    for (const r of rows) out[r.state] += 1;
    return out;
  }, [rows]);
  const reclaimable = stateCounts.no_agent + stateCounts.stale;

  // Prefer the live queries when they have RESOLVED — they are this render's truth
  // — and fall back to the snapshot only while they haven't, so the numbers don't
  // flash to zero on load.
  //
  // The test is `data !== undefined`, never `length ||`: a project with genuinely
  // zero pending IPs would otherwise fall through to a stale snapshot and the
  // legend would say "Waiting to push 9" above a tab labelled "(0)". Two numbers
  // for the same thing on one screen is precisely the defect this page exists to
  // remove.
  const liveKnown = entries.data ? rows.length : (project?.liveCount ?? null);
  const pendingKnown = pending.data ? pendingRows.length : (project?.pendingCount ?? 0);
  const cap = capacity(liveKnown, pendingKnown);
  const color = levelColor(cap.level);
  const lastRun = runRows[0];

  if (!projectKey) return null;

  return (
    <div className="page atlas-project">
      <Link className="atlas-back" to="/atlas-whitelist">
        ← Atlas IP whitelist
      </Link>

      <div className="atlas-head">
        <div className="atlas-head__main">
          <div className="atlas-head__titles">
            <h2>{project?.displayName || projectKey}</h2>
            {project && (
              <>
                <span className={`badge ${project.isActive ? 'confirmed' : 'quiet'}`}>
                  {project.isActive ? 'active' : 'inactive'}
                </span>
                <span className="badge quiet">
                  {project.hasCredentials ? 'credentials configured' : 'no credentials'}
                </span>
              </>
            )}
          </div>
          <div className="atlas-head__meta">
            <span className="mono">{projectKey}</span>
            {project?.projectId && (
              <>
                <span className="atlas-sep">·</span>
                <span className="mono">{project.projectId}</span>
              </>
            )}
            {project?.liveCountAt && (
              <>
                <span className="atlas-sep">·</span>
                <span>
                  read from Atlas {relTime(Date.now() - new Date(project.liveCountAt).getTime())} ago
                </span>
              </>
            )}
          </div>
        </div>

        <div className="atlas-head__actions">
          <div className="btn-row">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!project?.hasCredentials || reconcileState.loading}
              onClick={() => void reconcile({ variables: { projectKey } })}
              title={
                project?.hasCredentials
                  ? 'Add reported IPs that are missing from Atlas'
                  : 'Configure OAuth credentials first'
              }
            >
              {reconcileState.loading ? 'Reconciling…' : 'Reconcile now'}
            </button>
            <button
              type="button"
              className="btn"
              disabled={!project?.hasCredentials || refreshState.loading}
              onClick={() => void refresh({ variables: { projectKey } })}
              title="Re-read the live access list from Atlas"
            >
              {refreshState.loading ? 'Reading…' : 'Refresh from Atlas'}
            </button>
            <button
              type="button"
              className="btn"
              disabled={!project?.hasCredentials || testState.loading}
              onClick={() => void testConn({ variables: { projectKey } })}
            >
              {testState.loading ? 'Testing…' : 'Test connection'}
            </button>
          </div>
          {lastRun && (
            <div className="atlas-head__note">
              Last reconcile {relTime(Date.now() - new Date(lastRun.startedAt).getTime())} ago —
              added {lastRun.added}, removed {lastRun.removed}
            </div>
          )}
          {testState.data && (
            <div className={testState.data.testAtlasConnection.success ? 'atlas-head__note' : 'error'}>
              {testState.data.testAtlasConnection.success
                ? 'Connection OK'
                : testState.data.testAtlasConnection.error || 'Connection failed'}
            </div>
          )}
        </div>
      </div>

      <div className="atlas-grid">
        <div className="atlas-main">
          {/* ── Capacity ─────────────────────────────────────────────────── */}
          <section className="atlas-cap">
            <div className="atlas-cap__head">
              <span className="atlas-label">Access list capacity</span>
              <span className="atlas-cap__limit">Atlas hard limit</span>
            </div>

            <div className="atlas-cap__row">
              <span className="atlas-cap__n mono" style={{ color }}>
                {cap.known ? cap.live : '—'}
              </span>
              <span className="atlas-cap__of mono">/ {ATLAS_CAP}</span>
              <span className="atlas-cap__headline" style={{ color }}>
                {cap.known
                  ? `${cap.slotsLeft} slot${cap.slotsLeft === 1 ? '' : 's'} left`
                  : 'never read'}
              </span>
            </div>

            <div className="atlas-cap__bar">
              <span
                style={{ width: `${cap.livePct}%`, background: cap.known ? color : 'transparent' }}
              />
              <span className="atlas-cap__pend" style={{ width: `${cap.pendingPct}%` }} />
            </div>

            <div className="atlas-legend">
              <Legend label="In Atlas now" n={cap.known ? cap.live : null} color={color} />
              <Legend label="Waiting to push" n={cap.pending} color="var(--blue-soft-strong)" />
              <Legend label="Free after push" n={cap.known ? cap.free : null} color="var(--faint-deep)" />
            </div>

            {cap.known && (
              <div className={`atlas-note${cap.rejected ? ' atlas-note--crit' : ''}`}>
                <Icon name="alert" size={16} />
                <div>
                  <div className="atlas-note__title">
                    {cap.rejected
                      ? `The next reconcile needs ${cap.projected} slots and will be rejected`
                      : `The next reconcile will use ${cap.projected} of ${ATLAS_CAP} slots`}
                  </div>
                  <div className="atlas-note__body">
                    {cap.pending} reported IP{cap.pending === 1 ? '' : 's'} waiting to be pushed,
                    leaving {cap.free} free.{' '}
                    {/* Only claim something about reclaimability once the entry
                        table has actually loaded. Without credentials we never
                        read the list, and "nothing here is reclaimable" would be
                        an assertion about data we do not have. */}
                    {!entries.data ? (
                      <>The live list has not been read, so which entries are reclaimable is unknown.</>
                    ) : reclaimable > 0 ? (
                      <>
                        {reclaimable} entr{reclaimable === 1 ? 'y looks' : 'ies look'} reclaimable —{' '}
                        {stateCounts.no_agent} ha{stateCounts.no_agent === 1 ? 's' : 've'} no agent
                        behind {stateCounts.no_agent === 1 ? 'it' : 'them'} and {stateCounts.stale}{' '}
                        ha{stateCounts.stale === 1 ? 's' : 've'} not been seen recently.
                      </>
                    ) : (
                      <>Every entry has an agent reporting it — nothing here is reclaimable.</>
                    )}
                  </div>
                  {reclaimable > 0 && (
                    <button
                      type="button"
                      className="mini-btn"
                      onClick={() => {
                        setTab('');
                        setStateFilter('no_agent');
                      }}
                    >
                      Show {reclaimable} reclaimable entr{reclaimable === 1 ? 'y' : 'ies'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* ── Tabs ─────────────────────────────────────────────────────── */}
          <div className="tabs">
            {/* `—` rather than `(0)` when the query has not resolved: an unread
                list is unknown, and a zero in a tab label reads as a fact. */}
            <button className={activeTab === 'list' ? 'active' : ''} onClick={() => setTab('')}>
              Access list ({entries.data ? rows.length : '—'})
            </button>
            <button
              className={activeTab === 'pending' ? 'active' : ''}
              onClick={() => setTab('pending')}
            >
              Waiting to push ({pending.data ? pendingRows.length : '—'})
            </button>
            <button
              className={activeTab === 'history' ? 'active' : ''}
              onClick={() => setTab('history')}
            >
              Sync history
            </button>
          </div>

          {activeTab === 'list' && (
            <EntryTable
              rows={rows}
              loading={entries.loading}
              error={!!entries.error}
              hasCredentials={project?.hasCredentials !== false}
              counts={stateCounts}
              state={stateFilter}
              onState={setStateFilter}
            />
          )}
          {activeTab === 'pending' && <PendingTable rows={pendingRows} loading={pending.loading} />}
          {activeTab === 'history' && <HistoryTable rows={runRows} />}
        </div>

        {/* ── Right rail ─────────────────────────────────────────────────── */}
        <aside className="atlas-rail">
          <section className="atlas-rail__card">
            <div className="atlas-label">Mapped clusters</div>
            {mapped.length ? (
              <div className="atlas-chips">
                {mapped.map((c) => (
                  <span key={c} className="env-chip mono">
                    {c}
                  </span>
                ))}
              </div>
            ) : (
              <p className="muted text-xs">
                No cluster maps here — agents will report <code>no_targets</code>.
              </p>
            )}
            <Link className="text-xs" to="/atlas-whitelist/mappings">
              Edit mappings
            </Link>
          </section>

          <HeartbeatCard beats={beats.data?.atlasAgentHeartbeats ?? []} />
        </aside>
      </div>
    </div>
  );
}

function Legend({ label, n, color }: { label: string; n: number | null; color: string }) {
  return (
    <span className="atlas-legend__item">
      <span className="atlas-swatch" style={{ background: color }} />
      {label}
      <span className="mono">{n ?? '—'}</span>
    </span>
  );
}

/**
 * The unified table — the fix for two-tables-no-diff.
 *
 * One row per entry Atlas actually returns, with a State column encoding whether
 * anything stands behind it. The footnote matters as much as the rows: people read
 * this as "what we intend to push" unless told otherwise.
 */
function EntryTable({
  rows,
  loading,
  error,
  hasCredentials,
  counts,
  state,
  onState,
}: {
  rows: AtlasEntryRow[];
  loading: boolean;
  error: boolean;
  hasCredentials: boolean;
  counts: Record<string, number>;
  state: string;
  onState: (v: string) => void;
}) {
  const [q, setQ] = useUrlState('liveq', '', 'livepage');
  const [page, setPage] = useUrlNumber('livepage', 1);

  const needle = q.trim().toLowerCase();
  const filtered = rows.filter(
    (r) =>
      (!state || r.state === state) &&
      (!needle ||
        r.cidr.toLowerCase().includes(needle) ||
        (r.clusterName ?? '').toLowerCase().includes(needle) ||
        (r.nodeName ?? '').toLowerCase().includes(needle) ||
        (r.comment ?? '').toLowerCase().includes(needle)),
  );
  const pg = usePaged(filtered, 25, { page, onPage: setPage });

  if (!hasCredentials) {
    return (
      <p className="muted" style={{ marginTop: 14 }}>
        This project has no OAuth credentials stored, so its live list has never been read. Add
        them by editing the project.
      </p>
    );
  }
  if (error) {
    return (
      <>
        <p className="error" style={{ marginTop: 14 }}>
          Couldn&apos;t reach Atlas for this project&apos;s access list.
        </p>
        <p className="muted">
          The <strong>Waiting to push</strong> tab still shows what agents report. Try{' '}
          <strong>Test connection</strong> — a rejected credential is the usual cause.
        </p>
      </>
    );
  }
  if (loading && !rows.length) return <p className="muted" style={{ marginTop: 14 }}>Loading…</p>;
  if (!rows.length) {
    return (
      <EmptyState
        icon="shield"
        title="Atlas returned an empty access list"
        body="Nothing is whitelisted for this project yet. Map a cluster to it, then reconcile."
      />
    );
  }

  const FILTERS: { key: string; label: string }[] = [
    { key: '', label: 'All' },
    { key: 'in_sync', label: 'In sync' },
    { key: 'no_agent', label: 'No agent' },
    { key: 'stale', label: 'Stale' },
  ];

  return (
    <>
      <div className="filter-row atlas-filters">
        <input
          type="search"
          placeholder="Filter by IP, cluster or comment…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <span className="atlas-seg">
          {FILTERS.map((f) => (
            <button
              key={f.key || 'all'}
              type="button"
              className={state === f.key ? 'on' : ''}
              onClick={() => onState(f.key)}
            >
              {f.label}
              {f.key ? ` ${counts[f.key] ?? 0}` : ''}
            </button>
          ))}
        </span>
        <span className="filter-count">
          {filtered.length} of {rows.length}
        </span>
      </div>

      <div className="atlas-card">
        <table className="atlas-table">
          <thead>
            <tr>
              <th>IP / CIDR</th>
              <th>Source</th>
              <th>Comment in Atlas</th>
              <th>Last seen</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map((r) => {
              const mins = minutesSince(r.lastSeenAt);
              return (
                <tr key={r.cidr}>
                  <td className={`mono atlas-cidr${r.state === 'open_to_all' ? ' atlas-cidr--open' : ''}`}>
                    {r.cidr}
                  </td>
                  <td>
                    {r.clusterName || (r.state === 'open_to_all' ? 'open to the internet' : '—')}
                    {r.nodeName && <div className="atlas-sub mono">{r.nodeName}</div>}
                  </td>
                  <td className="mono atlas-comment">{r.comment || '—'}</td>
                  <td className={r.state === 'stale' ? 'atlas-stale' : 'muted'}>
                    {r.lastSeenAt && mins != null
                      ? `${relTime(mins * 60000)} ago`
                      : r.state === 'manual'
                        ? '—'
                        : 'node gone'}
                  </td>
                  <td>
                    <span className={`atlas-state atlas-state--${r.state}`} title={STATE_TITLE[r.state]}>
                      {STATE_LABEL[r.state]}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pager
        page={pg.page}
        pageCount={pg.pageCount}
        total={pg.total}
        start={pg.start}
        pageSize={pg.pageSize}
        onPage={pg.setPage}
      />
      <p className="atlas-foot muted">
        These are the records Atlas itself returns for this project, not what we intend to push. A
        record with no agent behind it still occupies one of the {ATLAS_CAP} slots.
      </p>
    </>
  );
}

function PendingTable({ rows, loading }: { rows: PendingIp[]; loading: boolean }) {
  if (loading && !rows.length) return <p className="muted" style={{ marginTop: 14 }}>Loading…</p>;
  if (!rows.length) {
    return (
      <EmptyState
        icon="check"
        title="Nothing waiting to push"
        body="Every IP agents report for this project is already in the Atlas access list."
      />
    );
  }
  return (
    <>
      <div className="atlas-card" style={{ marginTop: 14 }}>
        <table className="atlas-table">
          <thead>
            <tr>
              <th>IP / CIDR</th>
              <th>Cluster</th>
              <th>Node</th>
              <th>Reported</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="mono atlas-cidr">{r.ipCidr}</td>
                <td>{r.clusterName || '—'}</td>
                <td className="mono atlas-sub">{r.nodeName || '—'}</td>
                <td className="muted">
                  {relTime(Date.now() - new Date(r.lastSeenAt).getTime())} ago
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="atlas-foot muted">
        Agents reported these node IPs but they are not in the Atlas list yet. Until a reconcile
        pushes them, those pods stay <code>0/1</code> READY — readiness is gated on Atlas
        convergence.
      </p>
    </>
  );
}

/** Sync history, with the column that makes capacity a trend rather than a delta. */
function HistoryTable({ rows }: { rows: SyncRun[] }) {
  if (!rows.length) {
    return (
      <EmptyState
        icon="clock"
        title="No sync runs yet"
        body="Reconcile has never run for this project. Press Reconcile now to push the reported IPs."
      />
    );
  }
  return (
    <div className="atlas-card" style={{ marginTop: 14 }}>
      <table className="atlas-table">
        <thead>
          <tr>
            <th>Started</th>
            <th>Result</th>
            <th>Change</th>
            <th>List size after</th>
            <th>Triggered by</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const size = r.listSizeAfter;
            return (
              <tr key={r.id}>
                <td className="muted">{new Date(r.startedAt).toLocaleString()}</td>
                <td>
                  <span className={`badge ${r.status === 'ok' ? 'confirmed' : 'failed'}`}>
                    {r.status}
                  </span>
                  {r.errorMessage && <div className="error text-xs">{r.errorMessage}</div>}
                </td>
                <td className="mono">
                  +{r.added} −{r.removed} skip {r.skipped}
                </td>
                {/* NULL for every run recorded before the column existed — a gap is
                    honest, an invented number in a capacity trend is not. */}
                <td
                  className="mono"
                  style={{ color: size != null ? levelColor(capacity(size, 0).level) : undefined }}
                >
                  {size ?? '—'}
                </td>
                <td className="muted">{r.triggeredBy || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Agent heartbeat — new, and the other thing that used to fail silently.
 *
 * When an agent stops reporting, the reported set freezes and every number on this
 * page keeps describing a world that has moved on. Nothing errors; it just quietly
 * stops being true.
 */
function HeartbeatCard({ beats }: { beats: AtlasHeartbeat[] }) {
  const withMins = beats.map((b) => ({ ...b, mins: minutesSince(b.lastSeenAt) }));
  const silent = withMins.filter((b) => (b.mins ?? 0) > ATLAS_AGENT_STALE_MIN);

  return (
    <section className={`atlas-rail__card${silent.length ? ' atlas-rail__card--alert' : ''}`}>
      <div className="atlas-rail__head">
        <span className="atlas-label">Agent heartbeat</span>
        <span className="atlas-rail__thresh">alert &gt; {ATLAS_AGENT_STALE_MIN}m</span>
      </div>
      {!withMins.length ? (
        <p className="muted text-xs">
          No agents report into this project&apos;s clusters — nothing is keeping its IP list
          current.
        </p>
      ) : (
        <>
          <div className="atlas-beats">
            {withMins.map((b) => (
              <div className="atlas-beat" key={b.clusterName}>
                <span className="atlas-dot" style={{ background: heartbeatColor(b.mins) }} />
                <span className="mono atlas-beat__name">{b.clusterName}</span>
                <span className="atlas-beat__ago" style={{ color: heartbeatColor(b.mins) }}>
                  {b.mins != null ? `${relTime(b.mins * 60000)} ago` : '—'}
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs muted atlas-beat__note">
            {silent.length
              ? `${silent.length} agent${silent.length === 1 ? '' : 's'} silent past the ${ATLAS_AGENT_STALE_MIN}-minute threshold. Anything approved since then is queued, not applied — this page and the real Atlas list have drifted apart.`
              : `All agents reporting inside the ${ATLAS_AGENT_STALE_MIN}-minute threshold. What you see here matches Atlas.`}
          </p>
        </>
      )}
    </section>
  );
}
